import { NgFor, NgIf } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import type { ConversationSummary } from '../../../chat/core/hub-types';
import { CardPatient, PatientCardChatService } from '../../../chat/patient-card-chat.service';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConnectIconComponent } from './connect-icon.component';
import {
  agingStatus,
  claimStaleness,
  ConnectAgingItem,
  ConnectRequest,
  documentTitle,
  formatCents,
  formatDayMonth,
  sastTodayIso,
  StalenessChip,
  unsentPaperClaimsText,
} from './connect-dashboard.util';

/**
 * One open claim or invoice in an age bucket, as Connect shows it: what is still owed, what was billed, and what the
 * scheme and the patient owe and have paid, with the document's status as the accent. An older Connect sends one item
 * per patient instead, shown as before. The account opens in Connect; the patient file here. Where the practice has a
 * billing team on MedicAI Connect, Chat opens the billing chat about the patient (or asks who to chat with).
 */
@Component({
  selector: 'cx-aging-card',
  standalone: true,
  imports: [NgIf, NgFor, MatMenuModule, ConnectIconComponent],
  host: { class: 'cx-card-host' },
  template: `
    <div class="cx-aging" [class]="'cx-aging cx-tone-' + status.variant + (patientOwes ? ' cx-aging--owes' : '')" (click)="openAccount.emit()">
      <div class="cx-card-top">
        <span class="cx-aging-name">{{ item.patient.name }} {{ item.patient.surname }}</span>
        <span class="cx-chip cx-chip--upper" [class]="'cx-chip cx-chip--upper cx-pill-' + status.variant">{{ status.label }}</span>
      </div>
      <button type="button" class="cx-chip cx-pill-info cx-request-chip" *ngIf="request" (click)="openRequest.emit(request); $event.stopPropagation()">
        <cx-icon name="file-text"></cx-icon> Letter of motivation requested
      </button>

      <span class="cx-meta cx-meta--doc" *ngIf="title">{{ title }}</span>
      <span class="cx-meta" *ngIf="item.patient.medicalAid">{{ item.patient.medicalAid }} {{ item.patient.medicalAidPlan }}</span>
      <span class="cx-meta" *ngIf="item.patient.medicalAidNumber">Member number: {{ item.patient.medicalAidNumber }}</span>
      <span class="cx-meta" *ngIf="item.patient.accountNumber">Account No. {{ item.patient.accountNumber }}</span>

      <div class="cx-money" *ngIf="!title">
        <span class="cx-money-total">{{ rand(item.outstandingCents) }}</span>
        <span class="cx-money-split">
          Scheme {{ rand(item.schemeOutstandingCents) }} ·
          <span [class.cx-owes]="patientOwes">Patient {{ rand(item.patientOutstandingCents) }}</span>
        </span>
      </div>

      <ng-container *ngIf="title">
        <div class="cx-money">
          <span class="cx-money-label">Still owed</span>
          <span class="cx-money-total">{{ rand(item.outstandingCents) }}</span>
          <span class="cx-money-split" *ngIf="billedText">{{ billedText }}</span>
        </div>
        <table class="cx-money-table">
          <thead><tr><th></th><th>Owes</th><th>Paid</th></tr></thead>
          <tbody>
            <tr *ngIf="showScheme">
              <td>Scheme</td>
              <td [class]="tone(item.schemeOutstandingCents, 'cx-amount-owed')">{{ rand(item.schemeOutstandingCents) }}</td>
              <td [class]="tone(item.schemePaidCents, 'cx-amount-paid')">{{ rand(item.schemePaidCents ?? 0) }}</td>
            </tr>
            <tr *ngIf="showPatient">
              <td>Patient</td>
              <td [class]="tone(item.patientOutstandingCents, 'cx-owes')">{{ rand(item.patientOutstandingCents) }}</td>
              <td [class]="tone(item.patientPaidCents, 'cx-amount-paid')">{{ rand(item.patientPaidCents ?? 0) }}</td>
            </tr>
          </tbody>
        </table>
      </ng-container>

      <span class="cx-chip cx-chip--block cx-pill-warning" *ngIf="unsentPaper">{{ unsentPaper }}</span>
      <span class="cx-chip cx-chip--block" *ngIf="staleness as stale" [class]="'cx-chip cx-chip--block cx-pill-' + stale.tone">{{ stale.label }}</span>

      <span class="cx-age">
        {{ ageText }}
        <span class="cx-age-sub" *ngIf="item.oldestServiceDate || title">{{ submittedText }}</span>
      </span>

      <div class="cx-actions">
        <button type="button" class="cx-btn cx-btn--outline" (click)="openAccount.emit(); $event.stopPropagation()">
          <cx-icon name="wallet"></cx-icon> Open account
        </button>
        <button *ngIf="item.patient.clinicalPatientId" type="button" class="cx-btn cx-btn--ghost" (click)="openPatientFile.emit(); $event.stopPropagation()">
          <cx-icon name="user"></cx-icon> Open patient file
        </button>
        <div class="cx-chat" *ngIf="chatGroupId && item.patient.clinicalPatientId">
          <button type="button" class="cx-btn cx-btn--ghost" data-testid="card-chat" (click)="chat(); $event.stopPropagation()">
            <cx-icon name="chat"></cx-icon> Chat about patient
          </button>
          <button type="button" class="cx-btn cx-btn--ghost cx-chat-more" aria-label="Other chats about this patient"
            [matMenuTriggerFor]="chatMenu" (click)="loadChats(); $event.stopPropagation()">
            <cx-icon name="chevron-down"></cx-icon>
          </button>
        </div>
      </div>
      <mat-menu #chatMenu="matMenu" xPosition="before">
        <button mat-menu-item disabled *ngIf="chatsLoading">Looking for chats…</button>
        <button mat-menu-item *ngFor="let c of chats" (click)="openChat(c.id)">{{ chatTitle(c) }}</button>
        <button mat-menu-item (click)="newChat()">New chat…</button>
      </mat-menu>
    </div>
  `,
})
export class ConnectAgingCardComponent {
  @Input() set item(value: ConnectAgingItem) {
    this._item = value;
    this.patientOwes = (value.patientOutstandingCents ?? 0) > 0;
    this.status = agingStatus(value.worstStatus, this.patientOwes);
    this.staleness = claimStaleness(value.oldestSchemeDeadlineServiceDate, sastTodayIso());
    this.unsentPaper = unsentPaperClaimsText(value.unsentPaperClaimCount);
    this.title = documentTitle(value);
    if (this.title) {
      this.ageText = value.oldestServiceDate
        ? `Service ${value.daysSinceOldestService}d ago (${formatDayMonth(value.oldestServiceDate)})`
        : 'Service date unknown';
    } else {
      const count = `${value.openClaimCount} open ${value.openClaimCount === 1 ? 'document' : 'documents'}`;
      this.ageText = value.oldestServiceDate
        ? `${count} · oldest service ${value.daysSinceOldestService}d ago (${formatDayMonth(value.oldestServiceDate)})`
        : count;
    }
    const billed = value.totalCents != null ? `Billed ${formatCents(value.totalCents)}` : '';
    const writtenOff = (value.writtenOffCents ?? 0) > 0 ? `Written off ${formatCents(value.writtenOffCents)}` : '';
    this.billedText = [billed, writtenOff].filter(Boolean).join(' · ');
    this.showScheme = value.documentType === 'claim' || (value.schemeOutstandingCents ?? 0) > 0 || (value.schemePaidCents ?? 0) > 0;
    this.showPatient = value.documentType === 'invoice' || this.patientOwes || (value.patientPaidCents ?? 0) > 0;
    this.submittedText = value.oldestSubmittedDate ? `submitted ${formatDayMonth(value.oldestSubmittedDate)}` : 'not submitted';
  }
  get item(): ConnectAgingItem {
    return this._item;
  }
  private _item!: ConnectAgingItem;

  /** The practice, when it has a billing team on MedicAI Connect to chat with about the patient. */
  @Input() chatGroupId: string | null = null;
  /** What the billing office asked for this patient (a letter of motivation), shown as a chip that opens it. */
  @Input() request: ConnectRequest | null = null;
  @Output() openRequest = new EventEmitter<ConnectRequest>();

  @Output() openAccount = new EventEmitter<void>();
  @Output() openPatientFile = new EventEmitter<void>();

  patientOwes = false;
  status = agingStatus(null, false);
  staleness: StalenessChip | null = null;
  unsentPaper: string | null = null;
  ageText = '';
  submittedText = '';
  /** The document this card is about; null for a per-patient item from an older Connect. */
  title: string | null = null;
  billedText = '';
  showScheme = false;
  showPatient = false;

  /** The billing chats about the patient, for the menu (loaded when it opens). */
  chats: ConversationSummary[] = [];
  chatsLoading = false;

  constructor(private readonly patientChat: PatientCardChatService) {}

  /** Who the chat is about: this practice's patient. */
  private cardPatient(): CardPatient {
    const p = this.item.patient;
    return { id: String(p.clinicalPatientId), groupId: String(this.chatGroupId), title: `${p.name ?? ''} ${p.surname ?? ''}`.trim() };
  }

  chat(): Promise<void> {
    return this.patientChat.open(this.cardPatient());
  }

  async loadChats(): Promise<void> {
    this.chatsLoading = true;
    try {
      this.chats = await this.patientChat.chats(this.cardPatient());
    } catch {
      this.chats = [];
    } finally {
      this.chatsLoading = false;
    }
  }

  openChat(conversationId: string): Promise<void> {
    return this.patientChat.openChat(conversationId, this.cardPatient());
  }

  newChat(): Promise<void> {
    return this.patientChat.startNew(this.cardPatient());
  }

  chatTitle(c: ConversationSummary): string {
    return this.patientChat.titleOf(c);
  }

  /** Zero amounts recede; others take the class given. */
  tone(cents: number | null | undefined, className: string): string {
    return (cents ?? 0) > 0 ? className : 'cx-amount-zero';
  }

  rand(cents: number): string {
    return formatCents(cents);
  }
}
